'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import { ArrowBack, Home } from '@mui/icons-material'
import { Box, Button, Container, Stack, Typography, alpha, useTheme } from '@mui/material'

/**
 * Global 404 Not Found Page
 * Premium design with smooth animations and professional aesthetics.
 */
export default function NotFound() {
  const theme = useTheme()
  const router = useRouter()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.default',
        // Subtle background pattern/gradient
        backgroundImage:
          theme.palette.mode === 'dark'
            ? `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%)`
            : `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%)`,
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '8rem', sm: '12rem' },
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: -4,
                  // Gradient text effect
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.1))',
                  mb: 2,
                }}
              >
                404
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
              Page Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 4 }}>
              The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
             <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => router.back()}
                sx={{ minWidth: 200 }}
              >
                Go Back
              </Button>

              <Button
                component={Link}
                href="/"
                variant="contained"
                startIcon={<Home />}
                sx={{ minWidth: 200 }}
              >
                Back Home
              </Button>
            </Stack>
          </motion.div>
        </Stack>
      </Container>
    </Box>
  )
}


